import { BaseApiClient } from './base-client';

export interface LDAPStatus {
  running: boolean;
  user_count: number;
  base_dn: string;
  ldap_urls: Record<string, string>;
  tls_enabled: boolean;
  bound_to_public: boolean;
  enabled: boolean;
}

export interface LDAPConfig {
  base_dn: string;
  ldap_urls: Record<string, string>;
  admin_dn: string;
  bound_to_public: boolean;
  external_access_enabled: boolean;
  external_port?: number;
}

export interface LDAPBindingSettings {
  bind_to_public: boolean;
}

export interface LDAPUserStatus {
  full_name?: string;
  email?: string;
  password_last_changed?: string;
  account_expires?: string;
  ftp_quota_mb?: number;
  ftp_enabled: boolean;
  disk_usage_mb: number;
}

export interface LDAPBulkUpdateResult {
  status: 'success' | 'partial';
  successful: string[];
  failed: string[];
  total: number;
}

export class LDAPClient extends BaseApiClient {
  constructor() {
    super();
  }

  async getLDAPStatus(serviceId: number): Promise<LDAPStatus> {
    return this.get(`/api/v1/ldap/services/${serviceId}/ldap/status`);
  }

  async getLDAPConfig(serviceId: number): Promise<LDAPConfig> {
    return this.get(`/api/v1/ldap/services/${serviceId}/ldap/config`);
  }

  async updateLDAPBinding(serviceId: number, settings: LDAPBindingSettings): Promise<{
    status: string;
    bound_to_public: boolean;
    ldap_urls: Record<string, string>;
  }> {
    return this.post(`/api/v1/ldap/services/${serviceId}/ldap/bind-settings`, settings);
  }

  async getUserLDAPStatus(serviceId: number, username: string): Promise<LDAPUserStatus> {
    return this.get(`/api/v1/ldap/services/${serviceId}/ldap/users/${username}/status`);
  }

  async suspendUser(serviceId: number, username: string, suspend: boolean = true): Promise<{
    status: string;
    username: string;
    suspended: boolean;
  }> {
    return this.post(`/api/v1/ldap/services/${serviceId}/ldap/users/${username}/suspend`, { suspend });
  }

  async setUserExpiry(serviceId: number, username: string, expiryDate?: string): Promise<{
    status: string;
    username: string;
    expiry_date?: string;
  }> {
    return this.post(`/api/v1/ldap/services/${serviceId}/ldap/users/${username}/expiry`, { 
      expiry_date: expiryDate 
    });
  }

  async bulkUpdateUsers(
    serviceId: number, 
    users: string[], 
    operation: 'suspend' | 'activate' | 'delete'
  ): Promise<LDAPBulkUpdateResult> {
    return this.post(`/api/v1/ldap/services/${serviceId}/ldap/bulk-update`, {
      users,
      operation
    });
  }
}

export const ldapClient = new LDAPClient();